import React from 'react';
import { Link } from 'react-router-dom';
import {
  Icon,
  Button,
  FormInputText,
  FormInputPassword,
} from '@fashionnovateam/nova-ui-toolkit';
import validateEmail from '../../Lib/Utils/Helpers/validate-email';

export type LoginFormProps = {
  isInfluencer: boolean;
  loading: boolean;
  onLogin: (email: string, password: string) => void;
};

export function LoginForm(props: LoginFormProps) {
  const { isInfluencer, loading, onLogin } = props;

  const [errors, setErrors] = React.useState<{
    email?: string;
    password?: string;
  }>({});

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget as HTMLFormElement;
    const email = form.email.value;
    const password = form.password.value;
    const validEmail = validateEmail(email);
    const validPassword = password.length > 0;
    if (!validEmail || !validPassword) {
      setErrors({
        email: validEmail ? undefined : 'Please enter a valid email address.',
        password: validPassword ? undefined : 'Please enter a password.',
      });
      return;
    }
    setErrors({});
    try {
      await onLogin(email, password);
    } catch (error) {
      // If the error has a message property, use it, otherwise use a generic message
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'An error occurred during login';
      setErrors(() => ({
        password: errorMessage,
      }));
    }
  };

  const handleEmailBlur = (email: string) => {
    setErrors((prev) => ({
      ...prev,
      email: validateEmail(email)
        ? undefined
        : 'Please enter a valid email address.',
    }));
  };

  const handlePasswordBlur = (password: string) => {
    setErrors((prev) => ({
      ...prev,
      password: password.length > 0 ? undefined : 'Please enter a password.',
    }));
  };

  return (
    <form className="flex flex-col gap-6" onSubmit={handleSubmit} noValidate>
      <div className="flex flex-col gap-4">
        <FormInputText
          label="Email"
          name="email"
          inputId="email"
          type="email"
          disabled={loading}
          error={errors.email}
          onBlurValue={handleEmailBlur}
        />
        <div className="flex flex-col gap-3">
          <FormInputPassword
            label="Password"
            name="password"
            inputId="password"
            disabled={loading}
            error={errors.password}
            onBlurValue={handlePasswordBlur}
          />
          <Link
            to={isInfluencer ? '/influencer-password-reset' : '/password-reset'}
            className="w-full text-xs font-medium text-primary-700 hover:text-primary-900"
          >
            Forgot password?
          </Link>
        </div>
      </div>
      <div className="flex flex-col gap-3">
        <Button className="w-full" type="submit" loading={loading}>
          Sign in
          <Icon.ArrowRight />
        </Button>
        <a
          href={`${process.env.REACT_APP_API_TARGET}/authentication/onelogin/login`}
          className="w-full flex justify-center text-xs font-medium text-primary-700 hover:text-primary-900"
        >
          Sign in with SSO
        </a>
      </div>
    </form>
  );
}
